import { Fragment, useEffect, useState, type ComponentType } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { OrgRole } from '@ddp/shared';
import Button from '@carbon/react/es/components/Button/Button';
import Modal from '@carbon/react/es/components/Modal/Modal';
import Select from '@carbon/react/es/components/Select/Select';
import SelectItem from '@carbon/react/es/components/SelectItem/SelectItem';
import TextInput from '@carbon/react/es/components/TextInput/TextInput';
import RadioButtonGroup from '@carbon/react/es/components/RadioButtonGroup/RadioButtonGroup';
import RadioButton from '@carbon/react/es/components/RadioButton/RadioButton';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';
import Flag from '@carbon/icons-react/es/Flag';
import CloudUpload from '@carbon/icons-react/es/CloudUpload';
import Model from '@carbon/icons-react/es/Model';
import MachineLearningModel from '@carbon/icons-react/es/MachineLearningModel';
import Analytics from '@carbon/icons-react/es/Analytics';
import Rocket from '@carbon/icons-react/es/Rocket';
import ArrowRight from '@carbon/icons-react/es/ArrowRight';

import { useAuthStore } from '../../auth/store.js';
import { useT, type MessageKey } from '../../i18n/index.js';
import { HelpHint } from '../../components/HelpHint.js';
import { useProjects } from '../projects/use-projects.js';
import { ObjectivesSection } from './ObjectivesSection.js';
import { USE_CASE_CATALOG } from './catalog.js';

/** L'écriture (créer un projet) exige EDITOR+ ; aligné sur `ProjectsPanel` et l'API. */
const ROLE_RANK: Record<OrgRole, number> = {
  VIEWER: 0,
  ANNOTATOR: 1,
  EDITOR: 2,
  ADMIN: 3,
  OWNER: 4,
};

/**
 * Étapes du processus « du besoin au modèle déployé ». L'**objectif** ouvre la marche (ADR-0028) :
 * il n'est plus un onglet séparé mais la première étape, d'où part le flux guidé. Les libellés sont
 * volontairement courts (bandeau compact) et distincts des libellés longs du plan (`objective.step.*`).
 */
interface ProcessStep {
  id: string;
  icon: ComponentType<{ size?: number }>;
  labelKey: MessageKey;
}

const PROCESS_STEPS: readonly ProcessStep[] = [
  { id: 'objective', icon: Flag, labelKey: 'model.process.step.objective' },
  { id: 'data', icon: CloudUpload, labelKey: 'model.process.step.data' },
  { id: 'baseModel', icon: Model, labelKey: 'model.process.step.baseModel' },
  { id: 'train', icon: MachineLearningModel, labelKey: 'model.process.step.train' },
  { id: 'evaluate', icon: Analytics, labelKey: 'model.process.step.evaluate' },
  { id: 'deploy', icon: Rocket, labelKey: 'model.process.step.deploy' },
];

/**
 * Bloc « Créer un modèle » en tête de la page Model : une **vue processus** compacte qui montre le
 * chemin complet (objectif → données → modèle de base → entraîner → évaluer → déployer), suivie du
 * point d'entrée « Démarrer un objectif projet ». Sous le bandeau, les objectifs déjà en cours
 * (repris de l'ancien onglet « Objectifs », désormais fondu ici — ADR-0028).
 */
export function ModelProcessBlock() {
  const t = useT();
  const [starting, setStarting] = useState(false);

  return (
    <section className="ddp-mprocess" aria-label={t('model.process.title')}>
      <div className="ddp-mprocess__head">
        <h2 className="ddp-mprocess__title">
          {t('model.process.title')}
          <HelpHint textKey="help.model.process" />
        </h2>
        <p className="ddp-mprocess__intro">{t('model.process.intro')}</p>
      </div>

      {/* Bandeau d'étapes : la première (objectif) est le point de départ, mise en avant. */}
      <div className="ddp-mprocess__flow" role="list" aria-label={t('model.process.flowLabel')}>
        {PROCESS_STEPS.map((step, i) => {
          const Icon = step.icon;
          return (
            <Fragment key={step.id}>
              <div
                className="ddp-mprocess__step"
                role="listitem"
                data-entry={i === 0 ? '' : undefined}
              >
                <span className="ddp-mprocess__node" aria-hidden="true">
                  <Icon size={20} />
                </span>
                <span className="ddp-mprocess__step-idx">{i + 1}</span>
                <span className="ddp-mprocess__label">{t(step.labelKey)}</span>
              </div>
              {i < PROCESS_STEPS.length - 1 && (
                <ArrowRight className="ddp-mprocess__arrow" size={16} aria-hidden="true" />
              )}
            </Fragment>
          );
        })}
      </div>

      <div className="ddp-mprocess__cta">
        <Button size="md" renderIcon={ArrowRight} onClick={() => setStarting(true)}>
          {t('model.process.cta')}
        </Button>
        <span className="ddp-mprocess__cta-note">{t('model.process.ctaNote')}</span>
      </div>

      {/* Objectifs déjà définis dans l'organisation — rien ne s'affiche s'il n'y en a pas encore. */}
      <ObjectivesSection withHeading />

      {starting && <StartObjectiveModal onClose={() => setStarting(false)} />}
    </section>
  );
}

type StartMode = 'existing' | 'new';

/**
 * Modale « Démarrer un objectif projet ». L'objectif se définit sur un **projet** (ADR-0024/0028) :
 * on rattache donc le démarrage à un projet existant, ou on en crée un (rôle éditeur). Le catalogue
 * de **cas d'usage** y figure comme sous-catégorie, pour cadrer l'objectif avant de le saisir.
 * À la validation, on rejoint le détail du projet avec `#objectif`, qui y ouvre le formulaire.
 */
function StartObjectiveModal({ onClose }: { onClose: () => void }) {
  const t = useT();
  const navigate = useNavigate();
  const { projects, loading, create } = useProjects();
  const role = useAuthStore((s) => s.currentUser?.memberships[0]?.role ?? null);
  const canCreate = role !== null && ROLE_RANK[role] >= ROLE_RANK.EDITOR;

  const [mode, setMode] = useState<StartMode>('existing');
  const [projectId, setProjectId] = useState('');
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // À l'arrivée des projets : présélectionner le premier, et basculer en « création » s'il n'y en a
  // aucun (seul chemin possible) — à condition d'en avoir le droit.
  useEffect(() => {
    if (loading) return;
    const first = projects[0];
    if (first) {
      setProjectId((current) => current || first.id);
      setMode('existing');
    } else if (canCreate) {
      setMode('new');
    }
  }, [loading, projects, canCreate]);

  const goToObjective = (id: string) => {
    onClose();
    void navigate({ to: '/projects/$projectId', params: { projectId: id }, hash: 'objectif' });
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (mode === 'new') {
        const created = await create({ name: newName.trim() });
        goToObjective(created.id);
      } else if (projectId) {
        goToObjective(projectId);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
      setBusy(false);
    }
  };

  const noProjectsNoRight = !loading && projects.length === 0 && !canCreate;
  const submitDisabled =
    busy ||
    noProjectsNoRight ||
    (mode === 'new' ? newName.trim().length < 2 : projectId.length === 0);

  return (
    <Modal
      open
      size="md"
      modalHeading={t('model.startObjective.title')}
      primaryButtonText={busy ? t('data.action.saving') : t('model.startObjective.confirm')}
      secondaryButtonText={t('overview.projects.cancel')}
      primaryButtonDisabled={submitDisabled}
      onRequestClose={onClose}
      onRequestSubmit={() => void submit()}
    >
      <div className="ddp-start-obj">
        <p className="ddp-start-obj__intro">{t('model.startObjective.intro')}</p>

        {noProjectsNoRight ? (
          <InlineNotification
            kind="info"
            title={t('model.startObjective.noRightTitle')}
            subtitle={t('model.startObjective.noRightBody')}
            lowContrast
            hideCloseButton
          />
        ) : (
          <div className="ddp-start-obj__choice">
            {projects.length > 0 && canCreate && (
              <RadioButtonGroup
                legendText={t('model.startObjective.projectLegend')}
                name="start-objective-mode"
                valueSelected={mode}
                onChange={(value) => setMode(value as StartMode)}
              >
                <RadioButton
                  id="start-objective-existing"
                  value="existing"
                  labelText={t('model.startObjective.modeExisting')}
                />
                <RadioButton
                  id="start-objective-new"
                  value="new"
                  labelText={t('model.startObjective.modeNew')}
                />
              </RadioButtonGroup>
            )}

            {mode === 'existing' && projects.length > 0 && (
              <Select
                id="start-objective-project"
                labelText={t('model.startObjective.pickProject')}
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
              >
                {projects.map((project) => (
                  <SelectItem key={project.id} value={project.id} text={project.name} />
                ))}
              </Select>
            )}

            {mode === 'new' && canCreate && (
              <TextInput
                id="start-objective-name"
                labelText={t('model.startObjective.newName')}
                placeholder={t('overview.projects.namePlaceholder')}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            )}
          </div>
        )}

        {/* Cas d'usage : sous-catégorie de l'objectif, pour l'orienter (lecture seule ici). */}
        <div className="ddp-start-obj__usecases">
          <h3 className="ddp-start-obj__usecases-title">
            {t('model.startObjective.useCasesTitle')}
          </h3>
          <p className="ddp-start-obj__usecases-hint">{t('model.startObjective.useCasesHint')}</p>
          <ul className="ddp-start-obj__usecases-grid">
            {USE_CASE_CATALOG.map((useCase) => (
              <li key={useCase.id} className="ddp-start-obj__usecase">
                <span className="ddp-start-obj__usecase-name">{t(useCase.nameKey)}</span>
                <span className="ddp-start-obj__usecase-desc">{t(useCase.descKey)}</span>
              </li>
            ))}
          </ul>
        </div>

        {error && (
          <InlineNotification
            kind="error"
            title={t('common.error')}
            subtitle={error}
            lowContrast
            hideCloseButton
          />
        )}
      </div>
    </Modal>
  );
}
